import { useState } from 'react';
import {
  X, ArrowLeft, ArrowRight, Check, Copy, Mail, Loader2,
  Chrome, AppWindow, Apple, Briefcase, AtSign, MoreHorizontal,
  HelpCircle, Sparkles, ExternalLink,
} from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { logFunnel } from '../lib/funnel';

// Email-provider catalogue. Add new providers here and the wizard picks them
// up automatically (icon + instructions). Designed for easy extension —
// Exchange, Zoho, Proton, Fastmail can be appended without touching the UI.
const PROVIDERS = [
  {
    id: 'gmail',
    name: 'Gmail',
    icon: Chrome,
    color: 'text-red-500',
    bg: 'bg-red-50',
    settingsUrl: 'https://mail.google.com/mail/u/0/#settings/general',
    instructions: [
      'Open Gmail and click the gear icon (⚙) in the top right.',
      'Click "See all settings".',
      'Scroll down to the "Signature" section.',
      'Click "Create new" and give your signature a name.',
      'Click inside the editor and press Ctrl+V (Cmd+V on Mac) to paste.',
      'Scroll to the bottom and click "Save Changes".',
    ],
  },
  {
    id: 'outlook',
    name: 'Outlook',
    icon: AppWindow,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    settingsUrl: 'https://outlook.live.com/mail/0/options/mail/messageContent',
    instructions: [
      'Open Outlook Desktop: File → Options → Mail → Signatures. (Web: Settings → Mail → Compose and reply.)',
      'Click "New" and give your signature a name.',
      'Click inside the editor.',
      'Paste your signature (Ctrl+V on Windows, Cmd+V on Mac).',
      'Set it as the default for new messages.',
      'Click "Save".',
    ],
  },
  {
    id: 'm365',
    name: 'Microsoft 365',
    icon: Briefcase,
    color: 'text-indigo-600',
    bg: 'bg-indigo-50',
    settingsUrl: 'https://outlook.office.com/mail/options/mail/messageContent',
    instructions: [
      'Sign in to outlook.office.com.',
      'Click the gear icon → "View all Outlook settings".',
      'Go to Mail → Compose and reply.',
      'Click inside the signature editor and paste (Ctrl+V / Cmd+V).',
      'Tick the boxes to apply on new messages and replies.',
      'Click "Save".',
    ],
  },
  {
    id: 'apple',
    name: 'Apple Mail',
    icon: Apple,
    color: 'text-gray-700',
    bg: 'bg-gray-100',
    instructions: [
      'Open Mail on Mac → Mail menu → Settings (Cmd+,).',
      'Click the "Signatures" tab.',
      'Select your email account in the left column.',
      'Click "+" to create a new signature.',
      'Uncheck "Always match my default message font" (important!).',
      'Paste your signature (Cmd+V) and close Settings — it saves automatically.',
    ],
  },
  {
    id: 'yahoo',
    name: 'Yahoo Mail',
    icon: AtSign,
    color: 'text-violet-600',
    bg: 'bg-violet-50',
    settingsUrl: 'https://mail.yahoo.com/d/settings/3',
    instructions: [
      'Sign in to Yahoo Mail.',
      'Click "Settings" (gear icon) → "More Settings".',
      'Click "Writing email" in the left sidebar.',
      'Turn on "Show a rich text signature".',
      'Paste your signature into the editor (Ctrl+V / Cmd+V).',
      'Click "Save".',
    ],
  },
  {
    id: 'other',
    name: 'Other',
    icon: MoreHorizontal,
    color: 'text-slate-600',
    bg: 'bg-slate-100',
    instructions: [
      'Open your email client and find the signature settings.',
      'Look for "Signature" or "Email signature" in the Compose / Mail preferences.',
      'Create a new signature.',
      'Click inside the rich-text editor and paste (Ctrl+V / Cmd+V).',
      'Make sure HTML/rich-text formatting is enabled.',
      'Save the signature and send yourself a test email to confirm.',
    ],
  },
];

const TROUBLESHOOTING = [
  { title: 'Pasted as plain text?', body: 'Make sure your editor is in rich-text / HTML mode (look for a "Rich text" toggle near the signature box). Then paste again with Ctrl+V / Cmd+V.' },
  { title: 'Images missing?', body: "Some clients block external images by default. Send yourself a test email and click 'Display images' if prompted. Apple Mail: make sure 'Always match my default message font' is unchecked." },
  { title: 'Signature appears in compose but not on sent emails?', body: 'Save the signature first, then go back into Settings and set it as the default for both "New messages" and "Replies/Forwards".' },
  { title: 'On mobile?', body: 'Rich HTML signatures must be set up on desktop Gmail / Outlook first — they will then sync to the mobile app automatically.' },
];

/**
 * Guided signature-installation wizard. 4 steps:
 *   1. Choose provider
 *   2. Copy My Signature
 *   3. Provider-specific instructions
 *   4. Did it install? Yes / Need Help
 *
 * The wizard is purely guidance — no automation. Goal is to make installation
 * feel achievable in under 2 minutes.
 */
export const InstallSignatureWizard = ({ open, onClose, signatureId, onCompleted }) => {
  const [step, setStep] = useState(1);
  const [providerId, setProviderId] = useState(null);
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [needsHelp, setNeedsHelp] = useState(false);

  const provider = PROVIDERS.find(p => p.id === providerId);

  const reset = () => {
    setStep(1);
    setProviderId(null);
    setCopying(false);
    setCopied(false);
    setNeedsHelp(false);
  };

  const handleClose = () => {
    onClose();
    setTimeout(reset, 250);
  };

  const handleCopy = async () => {
    if (!signatureId) {
      toast.error('No signature to copy. Generate one in the Builder first.');
      return;
    }
    setCopying(true);
    try {
      const data = await api.exportSignature(signatureId);
      const html = data.html;
      try {
        const blob = new Blob([html], { type: 'text/html' });
        const clipboardItem = new ClipboardItem({ 'text/html': blob });
        await navigator.clipboard.write([clipboardItem]);
      } catch {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        tempDiv.style.position = 'fixed';
        tempDiv.style.left = '-9999px';
        tempDiv.contentEditable = 'true';
        document.body.appendChild(tempDiv);
        const range = document.createRange();
        range.selectNodeContents(tempDiv);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        document.execCommand('copy');
        sel.removeAllRanges();
        document.body.removeChild(tempDiv);
      }
      setCopied(true);
      toast.success('Signature copied!');
      setTimeout(() => setStep(3), 600);
    } catch {
      toast.error('Could not copy. Try again.');
    } finally {
      setCopying(false);
    }
  };

  if (!open) return null;

  const totalSteps = 4;
  const progress = step === 1 ? 'Choose Provider'
                  : step === 2 ? 'Copy Signature'
                  : step === 3 ? `Paste Into ${provider?.name || 'Email'}`
                  : 'All Done';

  return (
    <div
      className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/55 p-3 sm:p-6"
      onClick={handleClose}
      data-testid="install-wizard-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[92vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-label="Install Your Signature"
        data-testid="install-wizard-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-[#1e3a5f] via-[#1f7ab3] to-emerald-600 px-5 py-4 text-white relative flex-shrink-0">
          <button
            onClick={handleClose}
            aria-label="Close wizard"
            className="absolute top-3 right-3 w-7 h-7 rounded-md hover:bg-white/15 flex items-center justify-center"
            data-testid="install-wizard-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4" />
            <span className="text-[10px] font-bold uppercase tracking-[0.18em]">Install Your Signature</span>
          </div>
          <h2 className="text-lg font-bold font-['Outfit']" data-testid="install-wizard-title">
            {step === 4 ? 'How did it go?' : `Step ${step} of ${totalSteps} — ${progress}`}
          </h2>
          <p className="text-[12px] text-white/85 mt-0.5">Under 2 minutes — guided every step of the way.</p>

          {/* Progress bar */}
          <div className="mt-3 flex gap-1">
            {[1, 2, 3, 4].map(n => (
              <div
                key={n}
                className={`flex-1 h-1.5 rounded-full transition-colors ${n <= step ? 'bg-amber-300' : 'bg-white/20'}`}
              />
            ))}
          </div>

          {/* Status pills (steps completed) */}
          {step > 1 && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="install-wizard-progress-pills">
              {copied && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-emerald-500/90 text-white px-2 py-0.5 rounded-full">
                  <Check className="w-3 h-3" /> Signature Copied
                </span>
              )}
              {provider && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-white/15 text-white px-2 py-0.5 rounded-full">
                  <Check className="w-3 h-3" /> {provider.name} Selected
                </span>
              )}
            </div>
          )}
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {/* STEP 1: Choose provider */}
          {step === 1 && (
            <div data-testid="install-wizard-step-1">
              <p className="text-sm text-slate-600 mb-4">Pick the email client you'll be installing into.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {PROVIDERS.map(p => {
                  const Icon = p.icon;
                  const selected = providerId === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setProviderId(p.id)}
                      data-testid={`install-wizard-provider-${p.id}`}
                      className={`group relative flex flex-col items-center gap-2 px-3 py-4 rounded-xl border-2 transition-all ${
                        selected
                          ? 'border-emerald-500 bg-emerald-50 shadow-md'
                          : 'border-slate-200 hover:border-emerald-300 hover:shadow-sm'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg ${p.bg} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${p.color}`} />
                      </div>
                      <span className="text-xs font-semibold text-slate-800">{p.name}</span>
                      {selected && (
                        <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center">
                          <Check className="w-2.5 h-2.5 text-white" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 2: Copy signature */}
          {step === 2 && (
            <div className="text-center" data-testid="install-wizard-step-2">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mb-4">
                <Copy className="w-7 h-7 text-white" />
              </div>
              <p className="text-sm text-slate-600 mb-5">
                Click the green button to copy your signature to your clipboard.
              </p>
              <button
                onClick={handleCopy}
                disabled={copying || copied}
                className={`w-full inline-flex items-center justify-center gap-2 py-4 px-6 rounded-xl text-base font-bold transition-all ${
                  copied
                    ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-300'
                    : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/30'
                }`}
                data-testid="install-wizard-copy-btn"
              >
                {copying ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Copying…</>
                ) : copied ? (
                  <><Check className="w-5 h-5" /> Copied!</>
                ) : (
                  <><Copy className="w-5 h-5" /> Copy My Signature</>
                )}
              </button>
              <p className="text-xs text-slate-500 mt-3">
                {copied
                  ? 'Your signature has been copied and is ready to install.'
                  : 'Your signature will be copied as rich text — paste anywhere.'}
              </p>
            </div>
          )}

          {/* STEP 3: Provider instructions */}
          {step === 3 && provider && (
            <div data-testid="install-wizard-step-3">
              <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-100">
                <div className={`w-10 h-10 rounded-lg ${provider.bg} flex items-center justify-center`}>
                  <provider.icon className={`w-5 h-5 ${provider.color}`} />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">{provider.name}</p>
                  <p className="text-xs text-slate-500">Follow the steps — most users finish in under 60 seconds.</p>
                </div>
              </div>
              <ol className="space-y-2.5">
                {provider.instructions.map((line, i) => (
                  <li key={i} className="flex gap-3 text-sm text-slate-700" data-testid={`install-wizard-instruction-${i + 1}`}>
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <span className="leading-snug pt-0.5">{line}</span>
                  </li>
                ))}
              </ol>
              {provider.settingsUrl && (
                <a
                  href={provider.settingsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-800"
                  data-testid="install-wizard-open-settings"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open {provider.name} settings in a new tab
                </a>
              )}
            </div>
          )}

          {/* STEP 4: Did it work? */}
          {step === 4 && !needsHelp && (
            <div className="text-center" data-testid="install-wizard-step-4">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-3">
                <Mail className="w-7 h-7 text-emerald-600" />
              </div>
              <p className="text-base font-semibold text-slate-900 mb-1">Did your signature install successfully?</p>
              <p className="text-xs text-slate-500 mb-5">
                Send yourself a test email from {provider?.name || 'your email client'} to confirm.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => { logFunnel('install_completed'); if (onCompleted) onCompleted(); handleClose(); toast.success('Awesome! You\'re all set. Every email now works for you.'); }}
                  className="flex-1 py-3 px-4 rounded-xl font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-md transition-colors"
                  data-testid="install-wizard-success-yes"
                >
                  Yes — all done
                </button>
                <button
                  onClick={() => setNeedsHelp(true)}
                  className="flex-1 py-3 px-4 rounded-xl font-bold border-2 border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
                  data-testid="install-wizard-success-help"
                >
                  I Need Help
                </button>
              </div>
            </div>
          )}

          {step === 4 && needsHelp && (
            <div data-testid="install-wizard-help">
              <div className="flex items-center gap-2 mb-3">
                <HelpCircle className="w-5 h-5 text-amber-600" />
                <p className="text-sm font-bold text-slate-900">Troubleshooting tips</p>
              </div>
              <div className="space-y-3">
                {TROUBLESHOOTING.map((t, i) => (
                  <div key={i} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <p className="text-xs font-bold text-slate-800 mb-1">{t.title}</p>
                    <p className="text-xs text-slate-600 leading-snug">{t.body}</p>
                  </div>
                ))}
              </div>
              <a
                href="mailto:connectmail@getfreshpass.com?subject=Signature%20Installation%20Help"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 py-2.5 px-4 rounded-lg font-semibold bg-emerald-600 hover:bg-emerald-700 text-white text-sm transition-colors"
                data-testid="install-wizard-contact"
              >
                <Mail className="w-4 h-4" /> Contact Support
              </a>
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-between bg-white flex-shrink-0">
          <button
            onClick={() => setStep(s => Math.max(1, s - 1))}
            disabled={step === 1}
            className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-800 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="install-wizard-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <span className="text-xs text-slate-400">Step {step} of {totalSteps}</span>
          {step < 4 ? (
            <button
              onClick={() => {
                if (step === 1 && !providerId) {
                  toast.error('Pick an email provider to continue.');
                  return;
                }
                if (step === 2 && !copied) {
                  toast.error('Copy your signature first.');
                  return;
                }
                setStep(s => Math.min(4, s + 1));
              }}
              className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:text-emerald-800"
              data-testid="install-wizard-next"
            >
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <span className="text-sm font-semibold text-emerald-700">Last Step</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallSignatureWizard;
